<template>
    <div class="w-100 d-flex justify-content-center">
        <section class="w-100 rates-table-section">
            <div class="rates-table-container" v-if="hasResults">
                <section class="rates-table-filters-wrapper justify-content-between mt-3 mb-2">
                    <div class="container-lg">
                        <div class="row">
                            <SelectButton
                                :label="$t('results.sorting.sortBy')"
                                :alignLeft="true"
                                :borderless="true"
                                :options="sortOptions"
                            />
                        </div>
                    </div>
                </section>

                <section class="rates-table-wrapper">
                    <div class="container-lg">
                        <div class="row">
                            <component
                                :is="campaignType !== 'Credit Card' ? 'RatesCardDefault' : 'RatesCreditCardCommon'"
                                v-for="(ratesItem, index) in sortedRatesItems"
                                :key="index + Math.random().toString(16).slice(2)"
                                :uid="index"
                                :ratesItem="ratesItem"
                                class="col-12"
                            />
                        </div>
                    </div>
                </section>

                <section class="showMoreLenders t-bold" v-if="ratesItems.length < totalLenderCount">
                    <span @click="showMoreLenders"
                        >{{ $t('ratesTable.showMoreLenders') }}<i class="fas fa-chevron-down"></i
                    ></span>
                </section>

                <section class="lenderTableFooter">
                    <span v-html="$t('ratesTable.meta')"></span>
                </section>
            </div>

            <div v-else class="card title">
                <div class="card-body">
                    <h6 class="t-bold">{{ $t('ratesTable.lenderNotFound') }}</h6>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { useStore, mapGetters } from 'vuex'
import { url } from '@js/utilities/url.js'
import { addFormParams, addHeightStyling } from '@js/utilities/ratesTable.js'
import { networkErrorMessage } from '@js/utilities/error.js'

// lodash components
import sortBy from 'lodash/sortBy'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'
import split from 'lodash/split'

import chunk from 'lodash/chunk'
import concat from 'lodash/concat'
import merge from 'lodash/merge'

export default {
    setup() {
        const store = useStore()

        return {
            store,
        }
    },
    data() {
        return {
            ratesItems: [],
            networkError: null,
            initialLoad: true,
            allLenders: [],
            totalLenderCount: 0,
            settings: {
                isMobile: window.innerWidth <= 991,
                desktopLenderChunkSize: 10,
                mobileLenderChunkSize: 5,
            },
        }
    },
    mounted() {
        this.emitter.on('submitRatesTable', () => {
            this.search()
        })

        this.$nextTick(() => {
            this.search(true)
        })

        this.store.commit('translateRtwSortOptions')
    },
    computed: {
        ...mapGetters(['getRatesCurrentSortSelected', 'getRatesSortOptions']),
        sortOptions: function () {
            return this.getRatesSortOptions
        },
        hasResults() {
            return !isEmpty(this.ratesItems)
        },
        searching: function () {
            return this.store.state.ratesTable.searching
        },
        message() {
            return (
                this.networkError ||
                (this.searching ? 'Searching...' : 'No results to display. Please modify your search.')
            )
        },
        sortedRatesItems() {
            return orderBy(
                this.ratesItems,
                rateItem => {
                    if (this.getRatesCurrentSortSelected.field.split('.')[0] === 'promo_offer') {
                        return parseFloat(split(rateItem.promo_offer, ';')[1])
                    }

                    return parseFloat(
                        this.getRatesCurrentSortSelected.field.split('.').reduce((o, i) => o[i], rateItem),
                    )
                },
                this.getRatesCurrentSortSelected?.descending ? 'desc' : 'asc',
            )
        },
    },

    methods: {
        search: async function (initial = false) {
            this.store.commit('setSearching', true)
            this.initialLoad = true

            if (initial) {
                this.store.commit('setGeneralLoaderVisible', true)
            }

            this.networkError = null

            try {
                let params = {
                    campaign_code: this.store?.state?.apply?.data?.campaignCode?.value,
                    card_type:
                        window.CAMPAIGN_TYPE === 'Credit Card' ? this.store?.state?.apply?.data?.cardType?.value : null,
                }
                if (!initial) {
                    params = addFormParams(params, this.store)
                }

                const response = await axios.post(url('api/rates-table/search'), params)
                if (response?.data) {
                    if (initial) {
                        this.campaignType = window.CAMPAIGN_TYPE
                    }
                    const responseData = response?.data?.data?.results
                    if (responseData.length > 0 && this.settings.isMobile) {
                        this.totalLenderCount = responseData.length
                        this.allLenders = chunk(responseData, this.settings.mobileLenderChunkSize)
                        this.ratesItems = concat(this.ratesItems, this.allLenders[0])
                    }
                    if (responseData.length > 0 && !this.settings.isMobile) {
                        this.totalLenderCount = responseData.length
                        this.allLenders = chunk(responseData, this.settings.desktopLenderChunkSize)
                        this.ratesItems = concat(this.ratesItems, this.allLenders[0])
                    }

                    this.store.commit(
                        'setActiveSortOptionList',
                        this.campaignType == 'Credit Card' ? 'creditCardOnly' : 'default',
                    )

                    this.store.commit('setOfferResultData', this.ratesItems)
                }
            } catch (error) {
                this.networkError = networkErrorMessage(this.store, error)
                console.error(error)
            }

            addHeightStyling()

            this.store.commit('setSearching', false)
            this.store.commit('setGeneralLoaderVisible', false)
        },
        //move to store
        sortRatesItems: function (option) {
            const sortedRatesCards = sortBy(this.ratesItems, rateItem => {
                return option.field.split('.').reduce((o, i) => o[i], rateItem)
            })

            this.ratesItems = sortedRatesCards
        },
        showMoreLenders() {
            let chunkToAdd = null
            if (this.settings.isMobile) {
                chunkToAdd = this.ratesItems.length / this.settings.mobileLenderChunkSize
            } else {
                chunkToAdd = this.ratesItems.length / this.settings.desktopLenderChunkSize
            }

            this.ratesItems = concat(this.ratesItems, this.allLenders[chunkToAdd])
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.rates-table-container {
    margin: 20px auto;
    max-width: 540px;

    @media screen and (min-width: $bp-medium) {
        max-width: none;
    }

    @media screen and (min-width: $bp-large) {
        margin: 20px auto 10px;
        max-width: 970px;
    }

    .rates-table-filters-wrapper {
        display: flex;
    }

    .title {
        text-align: center;

        h1 {
            font-size: rem(30px);
            line-height: rem(36px);
            letter-spacing: -0.01em;
        }

        h6 {
            font-size: rem(18px);
            line-height: rem(22px);
            letter-spacing: -0.025em;
            color: var(--text-default);
            margin-top: 40px;
            margin-bottom: 40px;
        }
    }

    .showMoreLenders {
        color: var(--primary);
        margin-top: 20px;
        margin-bottom: 20px;
        text-align: center;
        cursor: pointer;

        @media screen and (min-width: $bp-large) {
            margin-top: 40px;
            margin-bottom: 40px;
        }

        svg {
            margin-left: 5px;
        }
    }

    .lenderTableFooter {
        color: var(--text-default);
        font-size: rem(14px);
        margin-bottom: 40px;
        text-align: center;
    }

    .no-results {
        padding: 50px;
        text-align: center;
    }
}
</style>
